import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// COMPONENTS
import Button from "@/main/components/shared/button/button";
import Box from "@/main/components/shared/box";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { AreaButton } from "@/main/components/shared/styles";

// SERVICES / REDUX / UTILS
import { getOptionsStatus } from "@/main/services/data/data";
import { KeyValue } from "@/main/models/common";
import {
	getDefaultCountryOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	mountReducerWithData,
	removeEmptyValues,
} from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

// SCREENS/COMPONENTS/CONTEXT LOCAL
import * as S from "./styles";
import { HolidaysRequest } from "@/main/models/request/holidaysRequest";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import ModalHoliday from "../modalHoliday/modalHoliday";
interface IFiltersSearchHolidaysProps {
	showFilters: boolean;
	formFilters: HolidaysRequest | null;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getDataHolidays: (filters: HolidaysRequest) => void;
}
// #region Constants
const INITIAL_FORM_STATE = {
	country: null as KeyValue | null,
	status: null as KeyValue | null,
	description: "" as string,
};

export type IFormFiltersSearchHolidays = typeof INITIAL_FORM_STATE;
// #endregion

function FiltersSearchHolidays({
	showFilters,
	setShowFilters,
	getDataHolidays,
	onResetSearch,
}: IFiltersSearchHolidaysProps) {
	// #region Reducers/Options
	const { countriesUser: countryReducer } = useCountryReducer();
	const optionsStatus = getOptionsStatus();
	// #endregion

	// #region Hooks
	const { control, reset, setValue, customHandleSubmit } = useFormQueryParams<IFormFiltersSearchHolidays>({
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmitFilter,
		mapDefaultValues: {
			country: countryReducer,
			status: mountReducerWithData(optionsStatus),
		},
	});
	const dispatch = useDispatch();
	// #endregion

	// #region States/Watch
	const optionDefaultCountry = getDefaultCountryOptionDrop(countryReducer.data);
	const optionDefaultStatus = optionsStatus.find((item) => item.key === StatusRegisterEnum.Active)!;

	// #endregion

	// #region Async Functions
	async function onSubmitFilter(data: IFormFiltersSearchHolidays) {
		const isStatusActive = Number(data.status?.key) === StatusRegisterEnum.Active;
		const mountRequest: Omit<HolidaysRequest, "pageIndex" | "pageSize"> = removeEmptyValues({
			guidCountry: String(data.country?.key),
			description: data.description,
			...(!isNullUndefined(data.status) && { idStatus: isStatusActive }),
		});

		getDataHolidays(mountRequest);
		setShowFilters(false);
	}

	// #endregion

	// #region Handle Functions
	function handleClear() {
		reset(INITIAL_FORM_STATE);
		onResetSearch();
		setValue("country", optionDefaultCountry);
		setValue("status", optionDefaultStatus);
	}
	// #endregion

	// #region UseEffects
	useEffect(() => {
		dispatch(getCountriesUser());
	}, []);
	// #endregion

	const disabledCountry = countryReducer?.data?.length === 1;

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						id="id-searchHoliday-select-country"
						nameCustom="country"
						label={getTranslateByKey("information.country")}
						isDefaultValueUnique
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						disabled={disabledCountry}
						onChangeCustom={() => {}}
					/>
					<KogInputTextControlledNew
						id="id-holiday-filter-txt-description"
						name="description"
						label={getTranslateByKey("information.description")}
						control={control}
					/>
					<KogAutoCompleteControlled
						id="id-holiday-filter-select-status"
						nameCustom="status"
						label={getTranslateByKey("information.status")}
						valueDefaultKey={optionDefaultStatus?.key}
						control={control}
						rulesCustom={{ required: false }}
						setValueCustom={setValue}
						options={optionsStatus}
						disabled={false}
						onChangeCustom={() => {}}
					/>
					<S.AreaButtons>
						<SecondaryButton id="id-holiday-btn-submit" type="submit">
							{getTranslateByKey("information.search")}
						</SecondaryButton>
						<SecondaryButton id="id-holiday-btn-clear" type="button" onClick={handleClear}>
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButtons>
				</S.Container>
			</form>
		</Box>
	);
}

function FiltersHolidaysWrapper(props: Omit<IFiltersSearchHolidaysProps, "showFilters" | "setShowFilters">) {
	//#region States
	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [isOpenModalHoliday, setIsOpenModalHoliday] = useState<boolean>(false);
	//#endregion

	//#region HandleFunctions
	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
	function handleNewHoliday(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		e.stopPropagation();
		setIsOpenModalHoliday(true);
	}

	function refreshSearchGrid() {
		const currentFilters = props.formFilters;
		if (!isNullUndefined(currentFilters)) {
			props.getDataHolidays(currentFilters);
		}
	}

	//#endregion

	const mergedProps: IFiltersSearchHolidaysProps = {
		...props,
		showFilters,
		setShowFilters,
	};
	return (
		<>
			<AreaButton>
				<Button onClick={handleFilters} id="id-holidays-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
				<Button onClick={handleNewHoliday} id="id-holidays-new-btn-filters">
					{getTranslateByKey("information.new.holiday")}
				</Button>
			</AreaButton>
			<FiltersSearchHolidays {...mergedProps} />

			<ModalHoliday
				isOpenModal={isOpenModalHoliday}
				closeModal={() => setIsOpenModalHoliday(false)}
				setIsOpenModal={setIsOpenModalHoliday}
				refreshSearchGrid={refreshSearchGrid}
			/>
		</>
	);
}
export default FiltersHolidaysWrapper;
